"use client";

import { FormEvent, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { submitDemoRequest, type DemoRequestIn } from "@/lib/public-forms/api";
import { track, EVENTS } from "@/lib/analytics";

export function DemoRequestSection(): JSX.Element {
  const { t } = useI18n();
  const searchParams = useSearchParams();

  const [formState, setFormState] = useState<{
    name: string;
    email: string;
    company: string;
    properties: string;
    website: string;
  }>({
    name: "",
    email: "",
    company: "",
    properties: "",
    website: "",
  });

  const [promoCode, setPromoCode] = useState("");
  const [promoVisible, setPromoVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    track(EVENTS.demoFormView, { section: "demo" });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Honeypot check: if website field is filled, spam detected
      if (formState.website) {
        setIsSuccess(true);
        setFormState({ name: "", email: "", company: "", properties: "", website: "" });
        setPromoCode("");
        setPromoVisible(false);
        return;
      }

      // Extract UTM parameters from URL
      const utmSource = searchParams.get("utm_source");
      const utmMedium = searchParams.get("utm_medium");
      const utmCampaign = searchParams.get("utm_campaign");

      const payload: DemoRequestIn = {
        email: formState.email,
        fullName: formState.name || undefined,
        companyName: formState.company || undefined,
        propertyCount: formState.properties ? parseInt(formState.properties, 10) : undefined,
        utmSource: utmSource || undefined,
        utmMedium: utmMedium || undefined,
        utmCampaign: utmCampaign || undefined,
        promoCode: promoCode.trim() || undefined,
      };

      await submitDemoRequest(payload);
      track(EVENTS.demoFormSubmit, {
        section: "demo",
        propertyCount: payload.propertyCount,
        hasCompany: Boolean(payload.companyName),
        utmSource: payload.utmSource,
      });
      setIsSuccess(true);
      setFormState({ name: "", email: "", company: "", properties: "", website: "" });
      setPromoCode("");
      setPromoVisible(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Не удалось отправить запрос";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-keiront-obsidian mb-3">
          {t("landing.demo.title")}
        </h2>
        <p className="text-lg text-slate-600 mb-6">
          {t("landing.demo.subtitle")}
        </p>

        <div className="mb-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
          {[
            t("landing.demo.bullet1"),
            t("landing.demo.bullet2"),
            t("landing.demo.bullet3"),
          ].map((b) => (
            <span key={b} className="inline-flex items-center gap-1.5">
              <span className="text-keiro-green font-bold">✓</span>
              {b}
            </span>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot field — invisible to users */}
          <input
            type="text"
            name="website"
            value={formState.website}
            onChange={(e) => setFormState({ ...formState, website: e.target.value })}
            style={{ display: "none" }}
            tabIndex={-1}
            autoComplete="off"
          />

          <input
            type="text"
            placeholder={t("landing.demo.namePlaceholder")}
            value={formState.name}
            onChange={(e) => setFormState({ ...formState, name: e.target.value })}
            required
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-keiront-green focus:ring-1 focus:ring-keiro-green"
          />

          <input
            type="email"
            placeholder={t("landing.demo.emailPlaceholder")}
            value={formState.email}
            onChange={(e) => setFormState({ ...formState, email: e.target.value })}
            required
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-keiront-green focus:ring-1 focus:ring-keiro-green"
          />

          <input
            type="text"
            placeholder={t("landing.demo.companyPlaceholder")}
            value={formState.company}
            onChange={(e) => setFormState({ ...formState, company: e.target.value })}
            required
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-keiront-green focus:ring-1 focus:ring-keiro-green"
          />

          <input
            type="text"
            placeholder={t("landing.demo.propertiesLabel")}
            value={formState.properties}
            onChange={(e) => setFormState({ ...formState, properties: e.target.value })}
            required
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-keiront-green focus:ring-1 focus:ring-keiro-green"
          />

          {/* Promo code (collapsed by default) */}
          {!promoVisible ? (
            <button
              type="button"
              onClick={() => setPromoVisible(true)}
              className="text-sm text-slate-500 hover:text-keiront-green underline underline-offset-2 transition-colors"
            >
              {t("landing.demo.promoLink") || "У меня есть промокод"}
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={t("landing.demo.promoPlaceholder") || "Введите промокод"}
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                maxLength={64}
                className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-keiront-green focus:ring-1 focus:ring-keiro-green"
              />
              <button
                type="button"
                onClick={() => { setPromoVisible(false); setPromoCode(""); }}
                className="px-3 py-3 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                aria-label={t("landing.demo.promoHide") || "Скрыть"}
              >
                ✕
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-keiront-green text-white font-semibold rounded-lg hover:bg-keiront-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t("landing.demo.submitting") || "Отправляем..." : t("landing.demo.submit")}
          </button>
        </form>

        {isSuccess && (
          <p className="mt-4 text-sm text-keiront-green font-medium">
            {t("landing.demo.thankYou")}
          </p>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-600 font-medium">
            {error}
          </p>
        )}

        <p className="mt-6 text-xs text-slate-500">
          {t("landing.demo.orEmail")}
        </p>
      </div>
    </section>
  );
}
